import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { TransportAssignmentType, TransportCapacityOrder } from '../transportAssignment.types';
import { getSelectedTransportAssignment, transportAssignmentsSlice } from '../../reducers/transportAssignmentsSlice';
import { SidebarHeader } from './SidebarHeader';
import { TransportCapacityOrderSidebarBodyContent } from './TransportCapacityOrderSidebarBodyContent';
import { SidebarExpander } from './SidebarExpander';
import { usePutAcknowledgementOfExternalTransportCapacityOrderChangesMutation } from '../../api/transportOrderApi';
import { FunctionComponent, useEffect } from 'react';
import { runInBackground } from '../../../../configuration/setup/backgroundActions';
import { TransportAssignmentConfirmationForm } from './TransportAssignmentConfirmationForm';

export const TransportCapacityOrderSidebar: FunctionComponent = () => {
    const selected = useAppSelector(getSelectedTransportAssignment);
    const capacityOrder = selected?.type === TransportAssignmentType.TRANSPORT_CAPACITY_ORDER ? selected : undefined;
    return <TransportCapacityOrderSidebarInternal transportCapacityOrder={capacityOrder} key={capacityOrder?.id} />;
};
const TransportCapacityOrderSidebarInternal = ({
    transportCapacityOrder,
}: {
    transportCapacityOrder: TransportCapacityOrder | undefined;
}) => {
    const [acknowledgeChanges] = usePutAcknowledgementOfExternalTransportCapacityOrderChangesMutation();
    const dispatch = useAppDispatch();

    useEffect(() => {
        return () => {
            if (transportCapacityOrder !== undefined && transportCapacityOrder.hasUnacknowledgedExternalChanges) {
                runInBackground(
                    acknowledgeChanges({
                        transportCapacityOrderId: transportCapacityOrder.transportCapacityOrderId,
                        hasUnacknowledgedExternalChanges: false,
                    }),
                );
            }
        };
    }, [acknowledgeChanges, transportCapacityOrder]);

    const sidebarProperties = {
        showHeaderBorder: true,
        resizable: true,
        position: Sidebar.RIGHT,
        minWidth: 400,
        maxWidth: 550,
        width: 550,
        fly: true,
    };

    if (transportCapacityOrder === undefined) {
        return <Sidebar {...sidebarProperties} closed />;
    }

    const closeSidebar = () => {
        dispatch(transportAssignmentsSlice.actions.deselectTransportAssignment());
    };

    const title = (
        <FormattedMessage
            id="outboundPortal.transportAssignments.sidebar.header.titleCapacityOrder"
            values={{
                amount: transportCapacityOrder.requestedTransportCapacity,
            }}
        />
    );
    const footerButton = (
        <div className="display-flex gap-10 flex-row-reverse">
            <button className="btn btn-default" type="button" onClick={closeSidebar}>
                <FormattedMessage id="outboundPortal.close" />
            </button>
        </div>
    );

    return (
        <Sidebar
            {...sidebarProperties}
            title={title}
            titleClassName="padding-left-10"
            closed={false}
            onClose={closeSidebar}
            footer={footerButton}
            footerClassName="margin-right-10"
        >
            <SidebarHeader
                transportAssignment={transportCapacityOrder}
                hasUnacknowledgedChanges={transportCapacityOrder.hasUnacknowledgedExternalChanges}
            />
            <SidebarExpander transportAssignment={transportCapacityOrder}>
                <TransportAssignmentConfirmationForm transportAssignment={transportCapacityOrder} />
            </SidebarExpander>
            <TransportCapacityOrderSidebarBodyContent transportCapacityOrder={transportCapacityOrder} />
        </Sidebar>
    );
};
